/**
 * Core HTTP plumbing for the backend API: base URL, timeouts, `ApiError`
 * construction and response mapping, and the shared `fetchJson` /
 * `fetchMultipart` helpers. Application code imports from `src/api.ts`.
 */
import type { BackendErrorCode } from '../types/backend';
import { DOMAIN_ERROR_MESSAGES_DA } from '../types/backend';
import { SEND_COOLDOWN_MS } from '../types/live';
import { clientSessionHeaders, rememberClientSessionFromResponse } from '../lib/clientSession';
import { notifyAdminUnauthorized, readAdminToken } from '../lib/adminToken';
import {
  notifyTeacherUnauthorized,
  readTeacherSession,
  TEACHER_SESSION_HEADER,
} from '../lib/teacherSession';

export const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8080';
export const USE_LIVE = import.meta.env.VITE_USE_LIVE_API === 'true';
export const USE_REPLAYED_SSE = import.meta.env.VITE_USE_REPLAYED_SSE === 'true';

export const REQUEST_TIMEOUT_MS = 30_000;
export const UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;
export const MAX_MESSAGE_LENGTH = 4_000;

export const UNAVAILABLE_MESSAGE = 'GymGPT er midlertidigt utilgængelig. Prøv igen.';
export const TIMEOUT_MESSAGE = 'Forespørgslen timed ud. Prøv igen.';
const VALIDATION_FALLBACK = 'Ugyldig forespørgsel';
export const CONTRACT_MESSAGE = 'Ugyldigt svarsformat fra serveren';
export const ADMIN_SESSION_EXPIRED_MESSAGE =
  'Din lærer-adgang er udløbet eller ugyldig. Log ind igen.';
export const TEACHER_SESSION_EXPIRED_MESSAGE = ADMIN_SESSION_EXPIRED_MESSAGE;
export const TEACHER_SESSION_REQUIRED_MESSAGE =
  'Log ind som lærer for at fortsætte.';
export const ADMIN_DISABLED_MESSAGE = 'Læreradgang er ikke aktiveret på serveren.';

export class ApiError extends Error {
  readonly httpStatus: number;
  readonly code: BackendErrorCode | null;
  readonly userMessage: string;
  readonly rateLimitCooldownMs: number | null;

  constructor(options: {
    httpStatus: number;
    code: BackendErrorCode | null;
    userMessage: string;
    rateLimitCooldownMs?: number | null;
  }) {
    super(options.userMessage);
    this.name = 'ApiError';
    this.httpStatus = options.httpStatus;
    this.code = options.code;
    this.userMessage = options.userMessage;
    this.rateLimitCooldownMs = options.rateLimitCooldownMs ?? null;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function validateOutgoingMessage(content: string): string {
  const trimmed = content.trim();
  if (!trimmed) {
    throw new ApiError({
      httpStatus: 422,
      code: null,
      userMessage: 'Beskeden må ikke være tom',
    });
  }
  if (trimmed.length > MAX_MESSAGE_LENGTH) {
    throw new ApiError({
      httpStatus: 422,
      code: null,
      userMessage: `Beskeden er for lang (maks. ${MAX_MESSAGE_LENGTH} tegn)`,
    });
  }
  return trimmed;
}

// Derived from the exhaustive message record so a code added to
// `BackendErrorCode` is recognized on the wire without a second list to keep
// in sync. An unrecognized admin 404 code would otherwise read as "admin
// disabled" and clear the operator session.
function isBackendErrorCode(value: unknown): value is BackendErrorCode {
  return (
    typeof value === 'string' &&
    Object.prototype.hasOwnProperty.call(DOMAIN_ERROR_MESSAGES_DA, value)
  );
}

export function parseValidationErrorMessage(detail: unknown): string {
  if (!Array.isArray(detail) || detail.length === 0) {
    return VALIDATION_FALLBACK;
  }
  const messages = detail
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }
      const msg = (entry as { msg?: unknown }).msg;
      return typeof msg === 'string' && msg.trim() ? msg.trim() : null;
    })
    .filter((msg): msg is string => msg !== null);
  return messages.length > 0 ? messages.join(' ') : VALIDATION_FALLBACK;
}

export function parseDomainErrorBody(body: unknown): {
  code: BackendErrorCode;
  message: string;
} | null {
  if (!body || typeof body !== 'object' || !('detail' in body)) {
    return null;
  }
  const detail = (body as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) {
    return null;
  }
  const code = (detail as { code?: unknown }).code;
  if (!isBackendErrorCode(code)) {
    return null;
  }
  const message = (detail as { message?: unknown }).message;
  return {
    code,
    message: typeof message === 'string' && message.trim()
      ? message.trim()
      : DOMAIN_ERROR_MESSAGES_DA[code],
  };
}

export function apiErrorFromParts(status: number, body: unknown): ApiError {
  if (status === 422) {
    const domain = parseDomainErrorBody(body);
    if (domain) {
      return new ApiError({
        httpStatus: 422,
        code: domain.code,
        userMessage: DOMAIN_ERROR_MESSAGES_DA[domain.code],
      });
    }
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    return new ApiError({
      httpStatus: 422,
      code: null,
      userMessage: parseValidationErrorMessage(detail),
    });
  }

  const domain = parseDomainErrorBody(body);
  if (domain) {
    return new ApiError({
      httpStatus: status,
      code: domain.code,
      userMessage: DOMAIN_ERROR_MESSAGES_DA[domain.code],
      rateLimitCooldownMs: domain.code === 'rate_limited' ? SEND_COOLDOWN_MS : null,
    });
  }

  if (status === 404) {
    return new ApiError({
      httpStatus: 404,
      code: 'not_found',
      userMessage: DOMAIN_ERROR_MESSAGES_DA.not_found,
    });
  }

  if (status >= 500) {
    return new ApiError({
      httpStatus: status,
      code: null,
      userMessage: UNAVAILABLE_MESSAGE,
    });
  }

  return new ApiError({
    httpStatus: status,
    code: null,
    userMessage: UNAVAILABLE_MESSAGE,
  });
}

export async function apiErrorFromResponse(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  return apiErrorFromParts(response.status, body);
}

function combineAbortSignals(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any(signals);
  }
  const controller = new AbortController();
  for (const signal of signals) {
    if (signal.aborted) {
      controller.abort();
      return controller.signal;
    }
    signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  return controller.signal;
}

type FetchJsonOptions = RequestInit & {
  signal?: AbortSignal;
  admin?: boolean;
  teacher?: boolean;
  session?: boolean;
};

type FetchMultipartOptions = {
  signal?: AbortSignal;
  admin?: boolean;
};

function buildRequestHeaders(
  inputHeaders: HeadersInit | undefined,
  admin: boolean,
  teacher: boolean,
  session: boolean,
): Headers {
  const headers = new Headers(inputHeaders);
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  if (admin && !headers.has('X-Admin-Token')) {
    const token = readAdminToken();
    if (token) {
      headers.set('X-Admin-Token', token);
    }
  }
  if (teacher && !admin && !headers.has(TEACHER_SESSION_HEADER)) {
    const teacherSession = readTeacherSession();
    if (teacherSession?.token) {
      headers.set(TEACHER_SESSION_HEADER, teacherSession.token);
    }
  }
  if (session && !admin && !teacher) {
    for (const [key, value] of Object.entries(clientSessionHeaders())) {
      if (!headers.has(key)) {
        headers.set(key, value);
      }
    }
  }
  return headers;
}

async function adminResponseError(
  response: Response,
  admin: boolean,
  requestAdminToken: string | null,
): Promise<ApiError> {
  if (admin && response.status === 401) {
    // A slower request issued with an old token must not invalidate a newer
    // session that completed login while this request was in flight.
    // Skip when no token was sent: both sides are null and would otherwise
    // look like "the current token expired".
    if (requestAdminToken && readAdminToken() === requestAdminToken) {
      notifyAdminUnauthorized('expired');
    }
    return new ApiError({
      httpStatus: 401,
      code: null,
      userMessage: ADMIN_SESSION_EXPIRED_MESSAGE,
    });
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (admin && response.status === 404 && parseDomainErrorBody(body) === null) {
    if (requestAdminToken && readAdminToken() === requestAdminToken) {
      notifyAdminUnauthorized('disabled');
    }
    return new ApiError({
      httpStatus: 404,
      code: null,
      userMessage: ADMIN_DISABLED_MESSAGE,
    });
  }

  return apiErrorFromParts(response.status, body);
}

async function teacherResponseError(
  response: Response,
  teacher: boolean,
  requestTeacherToken: string | null,
): Promise<ApiError> {
  if (teacher && response.status === 401) {
    // Same guard as admin 401: a request that never sent a session must not
    // look like "the current token expired" when both sides are null.
    if (requestTeacherToken && readTeacherSession()?.token === requestTeacherToken) {
      notifyTeacherUnauthorized('expired');
    }
    return new ApiError({
      httpStatus: 401,
      code: null,
      userMessage: requestTeacherToken
        ? TEACHER_SESSION_EXPIRED_MESSAGE
        : TEACHER_SESSION_REQUIRED_MESSAGE,
    });
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  if (teacher && response.status === 404 && parseDomainErrorBody(body) === null) {
    return new ApiError({
      httpStatus: 404,
      code: null,
      userMessage: ADMIN_DISABLED_MESSAGE,
    });
  }

  return apiErrorFromParts(response.status, body);
}

export function resolveDashboardAuth(): Pick<FetchJsonOptions, 'admin' | 'teacher'> {
  if (readAdminToken()) {
    return { admin: true };
  }
  if (readTeacherSession()) {
    return { teacher: true };
  }
  return {};
}

/** Encode one path segment so `/`, `?`, `#`, and `..` cannot change the request target. */
export function encodeApiSegment(value: string): string {
  return encodeURIComponent(value);
}

/**
 * Same-origin API paths only. Reject protocol-relative (`//host`) and scheme-bearing
 * values so `${API_BASE}${path}` cannot send credentials off-origin.
 */
export function isSafeApiPath(path: string): boolean {
  return path.startsWith('/') && !path.startsWith('//') && !path.includes('\\') && !path.includes('\0');
}

export function resolveApiUrl(path: string): string {
  if (!isSafeApiPath(path)) {
    throw new ApiError({
      httpStatus: 400,
      code: null,
      userMessage: CONTRACT_MESSAGE,
    });
  }
  return `${API_BASE}${path}`;
}

export async function fetchJson<T>(
  path: string,
  options: FetchJsonOptions = {},
): Promise<T> {
  const {
    admin = false,
    teacher = false,
    session = false,
    signal: outerSignal,
    headers: inputHeaders,
    ...fetchOptions
  } = options;
  const useSession = session && !admin && !teacher;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
  const signal = outerSignal
    ? combineAbortSignals([outerSignal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const headers = buildRequestHeaders(inputHeaders, admin, teacher, useSession);
    const requestAdminToken = admin ? headers.get('X-Admin-Token') : null;
    const requestTeacherToken = teacher ? headers.get(TEACHER_SESSION_HEADER) : null;
    const response = await fetch(resolveApiUrl(path), {
      ...fetchOptions,
      cache: 'no-store',
      signal,
      headers,
    });

    if (!response.ok) {
      if (teacher) {
        throw await teacherResponseError(response, teacher, requestTeacherToken);
      }
      throw await adminResponseError(response, admin, requestAdminToken);
    }

    if (useSession) {
      rememberClientSessionFromResponse(response);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError({
        httpStatus: 502,
        code: null,
        userMessage: CONTRACT_MESSAGE,
      });
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError({
        httpStatus: 408,
        code: null,
        userMessage: TIMEOUT_MESSAGE,
      });
    }
    throw new ApiError({
      httpStatus: 0,
      code: null,
      userMessage: UNAVAILABLE_MESSAGE,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchBlob(
  path: string,
  options: FetchJsonOptions = {},
): Promise<Blob> {
  const {
    admin = false,
    teacher = false,
    session = false,
    signal: outerSignal,
    headers: inputHeaders,
    ...fetchOptions
  } = options;
  const useSession = session && !admin && !teacher;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), REQUEST_TIMEOUT_MS);
  const signal = outerSignal
    ? combineAbortSignals([outerSignal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const headers = buildRequestHeaders(inputHeaders, admin, teacher, useSession);
    const requestAdminToken = admin ? headers.get('X-Admin-Token') : null;
    const response = await fetch(resolveApiUrl(path), {
      ...fetchOptions,
      cache: 'no-store',
      signal,
      headers,
    });

    if (!response.ok) {
      throw await adminResponseError(response, admin, requestAdminToken);
    }

    if (useSession) {
      rememberClientSessionFromResponse(response);
    }

    return await response.blob();
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError({
        httpStatus: 408,
        code: null,
        userMessage: TIMEOUT_MESSAGE,
      });
    }
    throw new ApiError({
      httpStatus: 0,
      code: null,
      userMessage: UNAVAILABLE_MESSAGE,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function fetchMultipart<T>(
  path: string,
  formData: FormData,
  options: FetchMultipartOptions = {},
): Promise<T> {
  const { admin = false, signal: outerSignal } = options;
  const timeoutController = new AbortController();
  const timeoutId = setTimeout(() => timeoutController.abort(), UPLOAD_TIMEOUT_MS);
  const signal = outerSignal
    ? combineAbortSignals([outerSignal, timeoutController.signal])
    : timeoutController.signal;

  try {
    const headers = buildRequestHeaders(undefined, admin, false, false);
    const requestAdminToken = admin ? headers.get('X-Admin-Token') : null;
    const response = await fetch(resolveApiUrl(path), {
      method: 'POST',
      signal,
      headers,
      body: formData,
    });

    if (!response.ok) {
      throw await adminResponseError(response, admin, requestAdminToken);
    }

    try {
      return (await response.json()) as T;
    } catch {
      throw new ApiError({
        httpStatus: 502,
        code: null,
        userMessage: CONTRACT_MESSAGE,
      });
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError({
        httpStatus: 408,
        code: null,
        userMessage: TIMEOUT_MESSAGE,
      });
    }
    throw new ApiError({
      httpStatus: 0,
      code: null,
      userMessage: UNAVAILABLE_MESSAGE,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Normalize an admin-endpoint failure to an `ApiError` carrying a Danish
 * fallback message: a `404 not_found` is remapped to the operation-specific
 * fallback (the resource is gone, not a generic "missing" route), any other
 * `ApiError` (domain code, 5xx, timeout) is preserved as-is, and a non-`ApiError`
 * (network/unexpected) becomes an `httpStatus: 0` unavailable error. Replaces the
 * per-operation try/catch boilerplate that previously repeated this shape.
 */
export function mapAdminError(error: unknown, fallbackMessage: string): ApiError {
  if (error instanceof ApiError) {
    if (error.httpStatus === 404 && error.code === 'not_found') {
      return new ApiError({
        httpStatus: 404,
        code: null,
        userMessage: fallbackMessage,
      });
    }
    return error;
  }
  return new ApiError({
    httpStatus: 0,
    code: null,
    userMessage: fallbackMessage,
  });
}

export const apiConfig = { API_BASE, USE_LIVE, USE_REPLAYED_SSE };
